import React, {
  useEffect,
  useRef,
  useContext,
  useCallback,
  useMemo
} from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import Toolbar from './Toolbar/Toolbar';
import BotBuilderContext from '../../context/BotBuilderContext/BotBuilderContext';
import PreviewModeContext from '../../context/PreviewModeContext/PreviewModeContext';
import VariableDropdownContext from './VariableDropdownContext';
import FloatingVariableDropdown from '../FloatingVariableDropdown/FloatingVariableDropdown';
import useFloatingTokenDropdown from './useFloatingTokenDropdown';
import {
  OutputFormat,
  createMarkdownExtensions,
  createPlaintextExtensions,
  handleMarkdownEnter,
  handlePlaintextEnter,
  insertPlaintextWithHardBreaks,
  readEditorContent,
  setEditorContent,
  transformTokenMarkup
} from './richTextEditorUtils';

const tokenAttrsFromFlags = (
  varName: string,
  isSystem: boolean,
  isGlobal: boolean
): { varName: string; slotName: string; varType: string } => {
  let varType = 'slot';
  if (isSystem) varType = 'system';
  else if (isGlobal) varType = 'global';
  return { varName, slotName: varName, varType };
};

interface RichTextEditorProps {
  textContent: string | null | undefined;
  onChange?: (content: string) => void;
  isEditable?: boolean;
  showToolbar?: boolean;
  className?: string;
  outputFormat?: OutputFormat;
  onPreview?: () => void;
  placeholder?: string;
  showVariableButton?: boolean;
  showPreviewButton?: boolean;
}

function RichTextEditor({
  textContent,
  onChange,
  isEditable = true,
  showToolbar = true,
  className = '',
  outputFormat = 'markdown',
  onPreview,
  placeholder = 'Type your response here...',
  showVariableButton = true,
  showPreviewButton = true
}: Readonly<RichTextEditorProps>): React.ReactElement {
  const { intent, slots, systemVariables } = useContext(BotBuilderContext);
  const ref = useRef<HTMLDivElement | null>(null);
  const { setIsPreviewMode } = useContext(PreviewModeContext);

  const extensions =
    outputFormat === 'markdown'
      ? createMarkdownExtensions(placeholder)
      : createPlaintextExtensions(placeholder);

  const editor = useEditor({
    content: '',
    editable: isEditable,
    parseOptions: { preserveWhitespace: true },
    extensions,
    coreExtensionOptions: {
      clipboardTextSerializer: {
        blockSeparator: outputFormat === 'plaintext' ? '\n' : '\n\n'
      }
    },
    onCreate: ({ editor: editorInstance }) => {
      if (!textContent) return;

      if (outputFormat === 'markdown') {
        setEditorContent(editorInstance, textContent, outputFormat);
      } else {
        insertPlaintextWithHardBreaks(
          editorInstance,
          transformTokenMarkup(textContent, outputFormat)
        );
      }

      if (isEditable) {
        const editorElement = editorInstance.view.dom;
        if (editorElement) {
          requestAnimationFrame(() => {
            editorElement.scrollTop = editorElement.scrollHeight;
          });
        }
      }
    },
    editorProps: {
      attributes: {
        class: `rich-text-editor ${className} px-3 py-2`,
        style: className?.includes('preview-mode')
          ? 'padding: 4px 8px;'
          : 'padding: 8px 12px;'
      },
      handleKeyDown: (view, event) => {
        if (!isEditable) return false;

        const isEnterKey =
          event.key === 'Enter' &&
          !event.shiftKey &&
          !event.ctrlKey &&
          !event.altKey;

        if (isEnterKey) {
          return outputFormat === 'plaintext'
            ? handlePlaintextEnter(view)
            : handleMarkdownEnter(view);
        }

        return false;
      },
      handlePaste: (view, event) => {
        if (!isEditable) return false;

        const { clipboardData } = event;
        if (clipboardData) {
          const text = clipboardData.getData('text/plain');
          if (text) {
            const normalizedText = text
              .replace(/\r\n/g, '\n')
              .replace(/\r/g, '\n')
              .replace(/\n{3,}/g, '\n\n');

            const { state, dispatch } = view;
            const { from, to } = state.selection;
            const transaction = state.tr.insertText(normalizedText, from, to);
            dispatch(transaction);

            return true;
          }
        }

        return false;
      }
    },
    onBlur: ({ editor: ed }) => {
      if (!isEditable || !onChange) return;

      setTimeout(() => {
        if (ref.current?.contains(document.activeElement)) return;

        onChange(readEditorContent(ed, outputFormat));
      }, 10);
    }
  });

  useEffect(() => {
    if (!editor) return undefined;

    if (textContent && !editor.getHTML().includes('data-type="slotToken"')) {
      setEditorContent(editor, textContent, outputFormat);
    }

    if (isEditable) {
      editor.commands.focus('end', { scrollIntoView: false });
    }
    return undefined;
  }, [editor, isEditable, textContent, outputFormat]);

  useEffect(() => {
    if (!editor || !textContent) return;

    const normalisedTextContent = textContent.trim();
    const normalisedCurrentContent = readEditorContent(
      editor,
      outputFormat
    ).trim();

    if (
      normalisedCurrentContent !== normalisedTextContent &&
      normalisedTextContent.length > 0
    ) {
      setEditorContent(editor, textContent, outputFormat);
    }
  }, [textContent, editor, outputFormat]);

  const {
    isOpen: isDropdownOpen,
    position: dropdownPosition,
    selectedTokenPos,
    open: openDropdown,
    close: closeDropdown
  } = useFloatingTokenDropdown(editor, ref);

  const updateExistingToken = useCallback(
    (varName: string, isSystem: boolean, isGlobal: boolean = false): void => {
      const attrs = tokenAttrsFromFlags(varName, isSystem, isGlobal);

      editor
        ?.chain()
        .focus()
        .command(({ tr, dispatch, state }: any) => {
          const node = state.doc.nodeAt(selectedTokenPos);
          if (node && node.type.name === 'slotToken') {
            tr.setNodeMarkup(selectedTokenPos, null, attrs);
            if (dispatch) dispatch(tr);
          }
          return true;
        })
        .run();

      closeDropdown();
    },
    [editor, selectedTokenPos, closeDropdown]
  );

  const insertNewToken = useCallback(
    (varName: string, isSystem: boolean, isGlobal: boolean = false): void => {
      editor
        ?.chain()
        .focus()
        .command(({ tr, dispatch, state }: any) => {
          const { from, to } = tr.selection;
          const { schema } = state;

          if (from !== to) {
            tr.delete(from, to);
          }

          const $pos = tr.doc.resolve(tr.selection.from);
          const { nodeBefore, nodeAfter } = $pos;

          const isAdjacentChip = (node: any): boolean =>
            !!node && node.type.name === 'slotToken';

          let insertPos = $pos.pos;

          if (isAdjacentChip(nodeBefore)) {
            tr.insertText(' ', insertPos);
            insertPos += 1;
          }

          const attrs = tokenAttrsFromFlags(varName, isSystem, isGlobal);
          tr.insert(insertPos, schema.nodes.slotToken.create(attrs));
          insertPos += 1;

          if (isAdjacentChip(nodeAfter)) {
            tr.insertText(' ', insertPos);
          }

          if (dispatch) dispatch(tr);
          return true;
        })
        .run();
    },
    [editor]
  );

  const handleInsertVariable = useCallback(
    (
      varName: string,
      isSystem: boolean = false,
      isGlobal: boolean = false
    ): void => {
      if (!editor || !isEditable) return;

      if (selectedTokenPos !== null) {
        updateExistingToken(varName, isSystem, isGlobal);
      } else {
        insertNewToken(varName, isSystem, isGlobal);
      }
    },
    [editor, isEditable, selectedTokenPos, updateExistingToken, insertNewToken]
  );

  const openVariableDropdown = useCallback(
    (
      _currentVarName: string,
      _isSystem: boolean,
      nodePos: number,
      element?: HTMLElement
    ): void => openDropdown(nodePos, element),
    [openDropdown]
  );

  const [, startTransition] = React.useTransition();

  const handlePreview = (): void => {
    if (onPreview) {
      onPreview();
      return;
    }

    if (!editor || !onChange) return;

    const output = readEditorContent(editor, outputFormat);

    startTransition(() => {
      onChange(output);
      setIsPreviewMode(true);
    });
  };

  const handleDropdownSelect = useCallback(
    (varName: string, isSystem: boolean, isGlobal: boolean = false): void => {
      handleInsertVariable(varName, isSystem, isGlobal);
    },
    [handleInsertVariable]
  );

  const contextValue = useMemo(
    () => ({ openVariableDropdown }),
    [openVariableDropdown]
  );

  return (
    <VariableDropdownContext.Provider value={contextValue}>
      <div
        className={`
          rich-text-editor-wrapper
          relative cursor-pointer bg-white rounded-lg w-full border border-gray-200
          ${!isEditable ? 'cursor-default px-2 py-1.5' : 'nodrag px-2 py-2'}
        `}
        ref={ref}
        onKeyDownCapture={(e) => {
          if (isEditable && (e.key === ' ' || e.key === 'Spacebar')) {
            e.stopPropagation();
          }
        }}
      >
        {editor && showToolbar && isEditable && (
          <Toolbar
            editor={editor}
            onInsertVariable={
              showVariableButton ? handleInsertVariable : undefined
            }
            onPreview={showPreviewButton ? handlePreview : undefined}
            intentSlots={intent?.slots}
            globalSlots={slots}
            systemVariables={systemVariables}
            outputFormat={outputFormat}
          />
        )}
        <div
          className='editor-scroll-container'
          onWheelCapture={(e) => {
            const editorEl = e.currentTarget.querySelector('.rich-text-editor');
            if (editorEl && editorEl.scrollHeight > editorEl.clientHeight) {
              e.stopPropagation();
            }
          }}
        >
          <EditorContent
            editor={editor}
            style={{ cursor: isEditable ? 'text' : 'default' }}
          />
        </div>
        {isDropdownOpen && dropdownPosition && (
          <FloatingVariableDropdown
            intentSlots={intent?.slots}
            globalSlots={slots}
            systemVariables={systemVariables}
            onSelect={handleDropdownSelect}
            onClose={closeDropdown}
            position={dropdownPosition}
            isOpen={isDropdownOpen}
          />
        )}
      </div>
    </VariableDropdownContext.Provider>
  );
}

export default RichTextEditor;
